import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Clock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

type Row = {
  day_of_week: number;
  is_available: boolean;
  target_hours: number;
};

const DEFAULT_ROWS: Row[] = DAYS.map((_, i) => ({
  day_of_week: i,
  is_available: i !== 0 && i !== 6,
  target_hours: 8,
}));

export default function TargetHours({
  companyId,
  isOwner,
  onChanged,
  embedded = false,
}: {
  companyId: string;
  isOwner: boolean;
  onChanged?: () => void;
  embedded?: boolean;
}) {

  const [rows, setRows] = useState<Row[]>(DEFAULT_ROWS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data, error } = await supabase
      .from("company_availability")
      .select("day_of_week, is_available, target_hours")
      .eq("company_id", companyId);
    setLoading(false);
    if (error) {
      toast.error("Could not load work day targets");
      return;
    }
    if (!data || data.length === 0) return;
    setRows(
      DEFAULT_ROWS.map((d) => {
        const found = data.find((r) => r.day_of_week === d.day_of_week);
        return found
          ? {
              day_of_week: d.day_of_week,
              is_available: found.is_available,
              target_hours: Number(found.target_hours ?? 8),
            }
          : d;
      })
    );
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const update = (day: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.day_of_week === day ? { ...r, ...patch } : r)));

  const save = async () => {
    setSaving(true);
    const { data: existing } = await supabase
      .from("company_availability")
      .select("id, day_of_week")
      .eq("company_id", companyId);
    const idByDay = new Map((existing ?? []).map((r) => [r.day_of_week, r.id]));

    for (const r of rows) {
      const payload = {
        company_id: companyId,
        day_of_week: r.day_of_week,
        is_available: r.is_available,
        target_hours: r.target_hours,
      };
      const id = idByDay.get(r.day_of_week);
      const { error } = id
        ? await supabase.from("company_availability").update(payload).eq("id", id)
        : await supabase.from("company_availability").insert(payload);
      if (error) {
        setSaving(false);
        toast.error("Could not save work day targets");
        return;
      }
    }
    setSaving(false);
    toast.success("Work day targets saved");
    onChanged?.();
  };

  const body = (
    <>
        <p className="text-xs text-muted-foreground">
          Hours of mow + drive time each crew should be scheduled for, per day.
        </p>

      <div className="overflow-hidden rounded border bg-background">
        <table className="w-full border-collapse text-xs">
          <thead className="bg-muted">
            <tr className="[&>th]:border-r [&>th]:border-border [&>th]:px-2 [&>th]:py-1 [&>th]:text-left [&>th]:font-semibold [&>th:last-child]:border-r-0">
              <th>Day</th>
              <th className="w-28">Working</th>
              <th className="w-32">Target hours</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.day_of_week}
                className="border-t border-border even:bg-muted/40 [&>td]:border-r [&>td]:border-border [&>td]:px-2 [&>td]:py-1 [&>td:last-child]:border-r-0"
              >
                <td className="font-medium">{DAYS[r.day_of_week]}</td>
                <td>
                  <label className="flex items-center gap-2 text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={r.is_available}
                      disabled={!isOwner}
                      onChange={(e) => update(r.day_of_week, { is_available: e.target.checked })}
                    />
                    {r.is_available ? "Yes" : "No"}
                  </label>
                </td>
                <td>
                  <Input
                    type="number"
                    min={0}
                    max={24}
                    step={0.5}
                    className="h-7 text-xs"
                    value={r.target_hours}
                    disabled={!isOwner || !r.is_available}
                    onChange={(e) =>
                      update(r.day_of_week, { target_hours: Number(e.target.value) || 0 })
                    }
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

        {isOwner && (
          <div className="flex justify-end">
            <Button size="sm" onClick={save} disabled={saving || loading}>
              {saving ? "Saving…" : "Save targets"}
            </Button>
          </div>
        )}
    </>
  );

  if (embedded) return <div className="space-y-3">{body}</div>;

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Clock className="h-4 w-4" />
          Work hour targets
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Target work day</DialogTitle>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}

